import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, merge, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, switchMap, tap, catchError, first, debounceTime, map } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  CloneDraftValveRequest,
  CreatePipeClassRequest,
  EsraPipingAPIClient,
  ServiceViewModel,
  ValveReleaseLookupViewModel
} from 'src/app/shared/models/autogenerated-piping';
import { Router } from '@angular/router';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { indicate } from 'src/app/operators';

/**
 * Data passed to the ValveSpecModal.
 * - 'clone': clone a published valve, navigates to edit page with all fields.
 * - 'create': create a new valve from scratch.
 * - 'cloneDraft': clone a draft valve to another BU via POST /Valve/CloneDraft.
 *   Only valveId and valveTag are required; no tag rename or application selection.
 */
export interface ValveSpecModalData {
  mode: 'clone' | 'create' | 'cloneDraft';
  valveId?: string;
  valveTag?: string;
  /**
   * Application of the source valve — required in cloneDraft mode so the release
   * lookup can fall back to the source application when the user does not pick a
   * target application override.
   */
  sourceApplication?: string;
}

export interface ValveSpecModalResult {
  mode: 'clone' | 'create';
  valveTagName: string;
  businessUnit: string;
  application: string;
  valveType?: string; // Only for 'create' mode
}

@Component({
  selector: 'app-valve-spec-modal',
  templateUrl: './valve-spec-modal.component.html',
  styleUrls: ['./valve-spec-modal.component.scss'],
  standalone: false
})
export class ValveSpecModalComponent implements OnInit, OnDestroy {

  specForm: FormGroup;
  submitting$ = new BehaviorSubject<boolean>(false);
  validatingTag$ = new BehaviorSubject<boolean>(false);
  tagTaken = false;
  tagChecked = false;
  tagCheckError = false;
  businessUnitsWithTag: string[] = []; // Business units where this valve tag exists

  /** Inline error shown in cloneDraft mode when the API returns 409 or 400. */
  cloneDraftError: string | null = null;

  /** Inline error shown when pipe class creation fails. */
  pipeClassCreateError: string | null = null;

  /**
   * Controls whether the modal creates a valve or a pipe class from scratch.
   * Only relevant when `isCreateMode` is true.
   */
  creationType: 'valve' | 'pipeClass' = 'valve';

  // Standalone controls for pipe class creation — kept outside specForm to avoid
  // affecting specForm.valid when in valve create mode.
  /** Pipe class name field. Required in pipe class create mode only. */
  pipeClassNameControl: FormControl = new FormControl('', [Validators.required]);
  /** Service selector. Required in pipe class create mode only. */
  serviceIdControl: FormControl = new FormControl('', [Validators.required]);

  // Dropdown options
  businessUnitOptions: string[] = [];
  applicationOptions: string[] = [];
  /** Active services loaded from the API, used in pipe class create mode. */
  serviceOptions: ServiceViewModel[] = [];

  // Release dropdown state
  /** Available PUBLISHED valve releases for the selected BU + Application. */
  releaseOptions: ValveReleaseLookupViewModel[] = [];
  /** True while the releases endpoint is in-flight. */
  isLoadingReleases$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  /** True when releases were loaded but none are available for the chosen BU + Application. */
  noReleasesAvailable: boolean = false;

  // Valve type options (loaded from API)
  valveTypeOptions: string[] = [];
  valveTypeSearch = '';

  get filteredValveTypeOptions(): string[] {
    const search = this.valveTypeSearch?.toLowerCase() ?? '';
    if (!search) {
      return this.valveTypeOptions;
    }
    return this.valveTypeOptions.filter(vt => vt.toLowerCase().includes(search));
  }

  /** Whether the modal is in "create new" mode vs "clone" mode */
  get isCreateMode(): boolean {
    return this.data.mode === 'create';
  }

  /** Whether the modal is in "clone draft to another BU" mode */
  get isCloneDraftMode(): boolean {
    return this.data.mode === 'cloneDraft';
  }

  /** Whether the modal is creating a new pipe class (toggle on top of create mode). */
  get isPipeClassMode(): boolean {
    return this.isCreateMode && this.creationType === 'pipeClass';
  }

  private readonly subscription = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<ValveSpecModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ValveSpecModalData,
    private esraPipingApiClient: EsraPipingAPIClient,
    private router: Router,
    private snackBarService: SnackBarService
  ) {
    const controls: Record<string, FormControl> = {
      valveTagName: new FormControl('', [Validators.required]),
      businessUnit: new FormControl('', [Validators.required]),
      application: new FormControl('', [Validators.required]),
      // Optional override for cloneDraft mode — no validator, null means inherit from source
      targetApplication: new FormControl<string | null>(null),
      // Release selection — required for create, clone, and cloneDraft modes
      releaseId: new FormControl('', [Validators.required])
    };

    // Add valveType control only for create mode
    if (this.data.mode === 'create') {
      controls['valveType'] = new FormControl('', [Validators.required]);
    }

    this.specForm = new FormGroup(controls);
  }

  get dialogTitle(): string {
    if (this.isPipeClassMode) { return 'create new pipe class'; }
    if (this.isCreateMode) { return 'create new spec'; }
    if (this.isCloneDraftMode) { return `clone draft: ${this.data.valveTag || 'valve'}`; }
    return `clone ${this.data.valveTag || 'valve'}`;
  }

  get dialogSubtitle(): string {
    if (this.isPipeClassMode) { return 'Enter the details for the new pipe class.'; }
    if (this.isCreateMode) { return 'Enter the details for the new valve spec.'; }
    if (this.isCloneDraftMode) { return 'Select the target Business Unit to clone this draft valve to.'; }
    return 'Enter a new name for the valve tag you want to clone.';
  }

  get submitButtonLabel(): string {
    return this.isCreateMode ? 'create' : 'clone';
  }

  get valveTagControl(): FormControl {
    return this.specForm.get('valveTagName') as FormControl;
  }

  get businessUnitControl(): FormControl {
    return this.specForm.get('businessUnit') as FormControl;
  }

  get applicationControl(): FormControl {
    return this.specForm.get('application') as FormControl;
  }

  get targetApplicationControl(): FormControl {
    return this.specForm.get('targetApplication') as FormControl;
  }

  get valveTypeControl(): FormControl {
    return this.specForm.get('valveType') as FormControl;
  }

  get releaseIdControl(): FormControl {
    return this.specForm.get('releaseId') as FormControl;
  }

  ngOnInit(): void {
    this.loadDropdownOptions();
    // Tag validation only applies to clone/create modes — in cloneDraft mode the tag
    // is fixed and uniqueness is enforced by the API (409 Conflict).
    if (!this.isCloneDraftMode) {
      this.setupTagValidation();
    }
    this.setupReleasesLoader();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Loads business unit, application, valve type, and service options from the API.
   */
  private loadDropdownOptions(): void {
    this.subscription.add(
      this.esraPipingApiClient.getAllBusinessUnits()
        .pipe(first())
        .subscribe({
          next: (units) => {
            this.businessUnitOptions = units || [];
          }
        })
    );

    this.subscription.add(
      this.esraPipingApiClient.getAllValveApplications()
        .pipe(first())
        .subscribe({
          next: (apps) => {
            this.applicationOptions = apps || [];
          }
        })
    );

    this.subscription.add(
      this.esraPipingApiClient.getComponentTypes(false, true)
        .pipe(first())
        .subscribe({
          next: (types) => {
            this.valveTypeOptions = types || [];
          }
        })
    );

    // Services are needed for pipe class creation. Load them eagerly so the
    // dropdown is ready immediately when the user switches to pipe class mode.
    this.subscription.add(
      this.esraPipingApiClient.getAllCurrentServices()
        .pipe(first())
        .subscribe({
          next: (services: ServiceViewModel[]) => {
            this.serviceOptions = (services || []).filter((s: ServiceViewModel) => s.isActive !== false);
          }
        })
    );
  }

  /**
   * Subscribes to BU + Application changes and reloads the available PUBLISHED valve
   * releases whenever either value changes. Resets the `releaseId` selection on every
   * reload so an obsolete ID from a previous combination is never submitted.
   *
   * In cloneDraft mode the effective application is the `targetApplication` override
   * when set, falling back to `data.sourceApplication` (the source valve's application).
   */
  private setupReleasesLoader(): void {
    const bu$ = this.businessUnitControl.valueChanges;

    // The application signal depends on the mode:
    // - create / clone: the required `application` control.
    // - cloneDraft: the optional `targetApplication` control, falling back to source.
    const app$ = this.isCloneDraftMode
      ? this.targetApplicationControl.valueChanges.pipe(
          map((v: string | null) => v ?? this.data.sourceApplication ?? '')
        )
      : this.applicationControl.valueChanges;

    this.subscription.add(
      combineLatest([bu$, app$]).pipe(
        distinctUntilChanged((prev, curr) => prev[0] === curr[0] && prev[1] === curr[1])
      ).subscribe(([bu, app]: [string, string]) => {
        // Reset release selection whenever the combination changes.
        this.releaseIdControl.setValue('');
        this.releaseOptions = [];
        this.noReleasesAvailable = false;

        if (bu && app) {
          this.loadReleasesForSelection(bu, app);
        }
      })
    );
  }

  /**
   * Fetches available releases for the given BU + Application and populates the
   * `releaseOptions` dropdown.
   * - In valve create/clone mode: uses `getValveReleasesForValveCreation` (PUBLISHED valve releases).
   * - In pipe class create mode: uses `getPipeClassReleasesForCreation` (PUBLISHED PIPE releases).
   * Sets `noReleasesAvailable` to true when the API returns an empty list.
   */
  private loadReleasesForSelection(bu: string, app: string): void {
    // Select the correct endpoint depending on what is being created.
    const releases$: Observable<ValveReleaseLookupViewModel[]> = this.isPipeClassMode
      ? this.esraPipingApiClient.getPipeClassReleasesForCreation(bu, app)
      : this.esraPipingApiClient.getValveReleasesForValveCreation(bu, app);

    this.subscription.add(
      releases$
        .pipe(indicate(this.isLoadingReleases$), first(), catchError(() => of([])))
        .subscribe((releases: ValveReleaseLookupViewModel[]) => {
          this.releaseOptions = releases ?? [];
          this.noReleasesAvailable = this.releaseOptions.length === 0;
          // Pre-select the only available release automatically when there is exactly one.
          if (this.releaseOptions.length === 1) {
            this.releaseIdControl.setValue(this.releaseOptions[0].id);
          }
        })
    );
  }

  /**
   * Sets up async validation — debounced check whether the valve tag is already taken
   * using getBusinessUnitsForValveTag (returns array of business units where tag exists).
   */
  private setupTagValidation(): void {
    // Only watch valveTagName and businessUnit — application and valveType are irrelevant for duplicate tag checks
    this.subscription.add(
      merge(
        this.valveTagControl.valueChanges,
        this.businessUnitControl.valueChanges
      ).pipe(
        debounceTime(1000),
        distinctUntilChanged(),
        tap(() => {
          this.validatingTag$.next(true);
          this.tagTaken = false;
          this.tagChecked = false;
          this.tagCheckError = false;
          this.businessUnitsWithTag = [];
        }),
        switchMap((_: string) => {
          const trimmedTag = this.valveTagControl.value?.trim();

          // Tag must be provided to check for duplicates
          if (!trimmedTag || trimmedTag.length === 0) {
            return of(null);
          }

          // Call API to get all business units where this valve tag exists
          return this.esraPipingApiClient.getBusinessUnitsForValveTag(trimmedTag).pipe(
            catchError(() => of(null))
          );
        })
      ).subscribe({
        next: (businessUnits: string[] | null) => {
          if (businessUnits === null) {
            // API error occurred
            this.tagCheckError = true;
            this.tagChecked = false;
            this.businessUnitsWithTag = [];
          } else {
            // Store the business units where this tag exists
            this.businessUnitsWithTag = businessUnits || [];

            // Check if the currently selected business unit has this tag
            const selectedBusinessUnit = this.businessUnitControl.value?.trim();
            if (selectedBusinessUnit && this.businessUnitsWithTag.includes(selectedBusinessUnit)) {
              // Selected business unit already has this tag - ERROR
              this.tagTaken = true;
            } else {
              // Selected business unit doesn't have this tag, but others might - this is OK
              // We'll show a warning in the UI if businessUnitsWithTag is not empty
              this.tagTaken = false;
            }

            this.tagChecked = true;
          }
          this.validatingTag$.next(false);
        },
        error: () => {
          this.tagCheckError = true;
          this.tagChecked = false;
          this.businessUnitsWithTag = [];
          this.validatingTag$.next(false);
        }
      })
    );
  }

  /**
   * Called when the user switches between valve and pipe class creation via the toggle.
   * Resets the release dropdown and reloads releases for the correct endpoint when
   * BU and Application are already selected.
   */
  onCreationTypeChange(): void {
    this.releaseIdControl.setValue('');
    this.releaseOptions = [];
    this.noReleasesAvailable = false;
    this.pipeClassCreateError = null;

    const bu: string = this.businessUnitControl.value;
    const app: string = this.applicationControl.value;
    if (bu && app) {
      this.loadReleasesForSelection(bu, app);
    }
  }

  isFormValid(): boolean {
    // Release selection is required in all modes.
    const releaseSelected: boolean = this.releaseIdControl.valid && !this.noReleasesAvailable;

    if (this.isPipeClassMode) {
      return (
        this.pipeClassNameControl.valid &&
        this.businessUnitControl.valid &&
        this.applicationControl.valid &&
        this.serviceIdControl.valid &&
        releaseSelected &&
        !this.submitting$.getValue()
      );
    }

    if (this.isCloneDraftMode) {
      // In cloneDraft mode only a target BU and a release are required.
      return this.businessUnitControl.valid && releaseSelected && !this.submitting$.getValue();
    }
    const baseValid = this.specForm.valid && !this.tagTaken && !this.tagCheckError && this.tagChecked && !(this.validatingTag$.getValue());
    return baseValid && releaseSelected;
  }

  /**
   * Check if there's a warning to display (valve tag exists in other business units)
   */
  hasWarning(): boolean {
    return !this.tagTaken &&
           !this.tagCheckError &&
           this.tagChecked &&
           this.businessUnitsWithTag.length > 0 &&
           !(this.validatingTag$.getValue());
  }

  /**
   * Get the warning message listing business units where this valve tag exists
   */
  getWarningMessage(): string {
    if (!this.hasWarning()) {
      return '';
    }

    const buList = this.businessUnitsWithTag.join(', ');
    return `Note: This valve tag already exists in the following business unit${this.businessUnitsWithTag.length > 1 ? 's' : ''}: ${buList}`;
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onConfirm(): void {
    if (!this.isFormValid()) { return; }

    if (this.isPipeClassMode) {
      this.pipeClassCreateError = null;
      const request: CreatePipeClassRequest = new CreatePipeClassRequest({
        name: this.pipeClassNameControl.value?.trim(),
        businessUnit: this.businessUnitControl.value,
        application: this.applicationControl.value,
        serviceId: this.serviceIdControl.value,
        releaseId: this.releaseIdControl.value
      });

      this.submitting$.next(true);
      this.subscription.add(
        this.esraPipingApiClient.createPipeClass(request)
          .pipe(first())
          .subscribe({
            next: (newPipeClassId: string) => {
              this.submitting$.next(false);
              this.snackBarService.showInfo('Pipe class created successfully.');
              this.dialogRef.close();
              this.router.navigate(['/pipingsummary', newPipeClassId], {
                queryParams: { isEditMode: 'true' }
              });
            },
            error: (err: any) => {
              this.submitting$.next(false);
              if (err?.status === 409) {
                this.pipeClassCreateError = 'A Draft pipe class with that name already exists for this BU and Application.';
              } else if (err?.status === 400) {
                this.pipeClassCreateError = err?.message || 'Invalid request. Please check the form fields.';
              } else {
                this.pipeClassCreateError = 'Failed to create the pipe class. Please try again.';
              }
            }
          })
      );
      return;
    }

    if (this.isCloneDraftMode) {
      this.cloneDraftError = null;
      const targetBusinessUnit: string = this.businessUnitControl.value;
      // Send targetApplication only when the user explicitly selects one;
      // null means the clone inherits the application from the source valve.
      const targetApplication: string | undefined = this.targetApplicationControl.value ?? undefined;
      const releaseId: string = this.releaseIdControl.value;
      const request: CloneDraftValveRequest = new CloneDraftValveRequest({
        sourceValveId: this.data.valveId,
        targetBusinessUnit,
        targetApplication,
        releaseId
      });

      this.submitting$.next(true);
      this.subscription.add(
        this.esraPipingApiClient.cloneDraftValve(request)
          .pipe(first())
          .subscribe({
            next: (newValveId: string) => {
              this.submitting$.next(false);
              this.snackBarService.showInfo('Draft valve cloned successfully.');
              this.dialogRef.close();
              // Navigate to the newly created draft valve's edit page
              this.router.navigate(['/piping/valve/edit'], {
                queryParams: { mode: 'edit', id: newValveId }
              });
            },
            error: (err: any) => {
              this.submitting$.next(false);
              if (err?.status === 409) {
                this.cloneDraftError = 'A draft already exists for this valve tag in the selected business unit.';
              } else {
                this.cloneDraftError = 'Failed to clone the draft. Please try again.';
              }
            }
          })
      );
      return;
    }

    const tag         = this.valveTagControl.value?.trim();
    const businessUnit = this.businessUnitControl.value;
    const application  = this.applicationControl.value;

    this.dialogRef.close();   // close without result — navigation handles the rest

    const releaseId: string = this.releaseIdControl.value;

    if (this.data.mode === 'clone') {
      this.router.navigate(['/piping/valve/edit'], {
        queryParams: {
          mode: 'clone',
          id: this.data.valveId,
          tag,
          businessUnit,
          application,
          releaseId
        }
      });
    } else {
      this.router.navigate(['/piping/valve/edit'], {
        queryParams: {
          mode: 'create',
          tag,
          businessUnit,
          application,
          type: this.valveTypeControl.value,
          releaseId
        }
      });
    }
  }
}
